/* global consoleLogData */

// TODO: extend for all anchor boxes, not just home page

/*******************************************************
 *
 * Notes:
 *  1. This file is responsible for managing the anchor boxes on the front page.
 *  2. The anchor boxes are used to navigate to different sections of the page.
 *  3. The primary anchor box is displayed by default, while the secondary anchor box is hidden.
 *  4. The secondary anchor box is shown when the user scrolls past the primary anchor box.
 *  5. The active tag is updated based on the user's interaction with the anchor boxes.
 *      - Multiple filters exist as part of the Anchor Box. The functionality is defined in other scripts.
 *      - The scripts include assets/source/js/main_v5.js, partials/query_loae_more_posts.js, and partials/user-functions.js.
 *
 *******************************************************/


export default class anchorBox {
    activeTag = {active: null};
    childrenClassName = 'tag-link';
    elemsArr = [];
    eventHandler = (e) => this.#updateActiveTag(e);
    parentElement;
    parentSelector = '.tag-links';
    secondaryBoxElement;

    constructor(parentSelector, childrenClassName) {
        this.parentSelector = parentSelector ?? this.parentSelector;
        this.childrenClassName = childrenClassName ?? this.childrenClassName;
        this.secondaryBoxElement = document.querySelector('.anchor-box-secondary') ?? false;
        this.elemsArr = [document.querySelector('.anchor-box'), this.secondaryBoxElement];
        this.#tagScrollInit();

        if (this.secondaryBoxElement !== false) {
            this.#secondaryBoxHandler();
        }
    }

    /**
     * Initializes the tag scroll functionality.
     *
     * This method sets up event listeners to handle horizontal scrolling of a parent element
     * when its child elements are touched and dragged. It calculates the difference in X position
     * between the previous touch and the current touch to determine the scroll amount.
     *
     * @private
     * @method #tagScrollInit
     * @memberof AnchorBox
     *
     * @example
     * // Assuming this method is called within the context of an AnchorBox instance
     * this.#tagScrollInit();
     *
     * @fires consoleLogData
     *
     * @description
     * - Logs the initialization message.
     * - Retrieves the class name of the child elements.
     * - Selects the parent element using the provided selector.
     * - Exits if the parent element is not found.
     * - Defines helper functions to calculate touch differences and handle scrolling.
     * - Adds event listeners for touchmove and touchend events.
     */
    #tagScrollInit() {
        consoleLogData('anchor-box.js #tagScrollInit firing...');

        // Get the class name of the children elements
        let children = this.childrenClassName;

        // Select the parent element using the parent selector
        let parentElement = document.querySelector(this.parentSelector) || '';

        // If the parent element is not found, exit the function
        if (parentElement === '') {
            return;
        }

        // Function to calculate the difference in X position between the previous touch and the current touch
        const diff = (e) => Math.floor(prevX - e.targetTouches[0]?.clientX);

        // Variable to store the X position of the previous touch
        let prevX = null;

        // Function to reset the previous X position to null
        function resetPrevX() {
            prevX = null;
        }

        // Function to handle the scroll of the tags
        function handleTagsScroll(e) {
            // If the target element does not have the children class, exit the function
            if (!e.target.classList.contains(children)) {
                return;
            }

            // If prevX is null, set it to the current touch's X position and exit the function
            if (prevX === null) {
                return prevX = e.targetTouches[0].clientX;
            }

            // Scroll the parent element by the difference in X position
            parentElement.scrollBy(diff(e), 0);

            // Update prevX to the current touch's X position
            prevX = e.targetTouches[0].clientX;
        }

        // Add event listeners for touchmove and touchend events
        document.addEventListener('touchmove', handleTagsScroll);
        document.addEventListener('touchend', resetPrevX);
    }

    /**
     * Handles the visibility of secondary anchor boxes based on scroll and resize events.
     *
     * This function sets up event listeners for scroll and resize events to manage the visibility
     * of anchor boxes and content cards on the page. It ensures that the secondary anchor box is
     * displayed when certain conditions are met and hides it otherwise.
     *
     * @function
     * @name #secondaryBoxHandler
     *
     * @description
     * - Initializes anchor boxes and content cards.
     * - Checks if the current page is the front page.
     * - Prepares scroll event listener if conditions are met.
     * - Handles scroll events to toggle visibility of anchor boxes.
     * - Cleans up event listeners and resets visibility classes.
     * - Registers a resize listener to manage scroll event listener based on window width.
     *
     * @example
     * // Usage
     * #secondaryBoxHandler();
     */
    #secondaryBoxHandler() {
        consoleLogData('anchor-box.js #secondaryBoxHandler firing...');
        let anchorBoxes = {length: 0};
        let contentCards = { length: 0 };
        let carouselHighlights = { length: 0 };
        let prepped = false;
        let anchorBoxTriggerHeight = 168; // default trigger height

        function isFrontPage() {
            return document.body.classList.contains('is_home_page');
        }

        function prepScroll() {
            anchorBoxes = document.querySelectorAll('.anchor-box');
            contentCards = document.querySelectorAll('.content-card');
            carouselHighlights = document.querySelectorAll('.carousel-highlights');
            if (isFrontPage() && prepped === false) {
                // prevent registering the event listener multiple times
                window.addEventListener('scroll', handleScroll);
                prepped = true;
            }
        }

        function handleScroll() {
            // Check if there is a carousel on the page
            if (carouselHighlights.length > 0) {
                // Get the height of the carousel element
                const carouselHeight = carouselHighlights[0].getBoundingClientRect().height;
                // adjust the trigger height if there is a carousel
                anchorBoxTriggerHeight = 168 + carouselHeight;
            }

            // these checks were for react but we do need to somewhat
            // make sure there are enough cards to scroll and show the next anchor box
            if (contentCards.length < 5) {
                contentCards = document.querySelectorAll('.content-card');
            }

            // we don't need this handleScroll function if we don't have a secondary anchorbox
            // this didn't return early because of react re-renders
            if (anchorBoxes.length < 2) {
                anchorBoxes = document.querySelectorAll('.anchor-box');
            }

            // if (
            // the current top of the content card is not yet aligned with the anchor box
            // the scrollY is greater than the bottom of the second anchor box
            // additionally the second anchor box is fixed just below the primary at this time
            // window is at the top of the screen, also the comment is out of date and in reference to nextjs code
            if (
                contentCards[5]?.getClientRects()[0]?.top < anchorBoxTriggerHeight ||
                window.scrollY > anchorBoxes[1]?.getClientRects()[0]?.bottom ||
                (window.scrollY === 0 && anchorBoxes[1].classList.contains('visible')) //when the dialog pops up
            ) {
                anchorBoxes[1]?.classList?.remove('hidden');
                anchorBoxes[1]?.classList?.add('visible');
                anchorBoxes[0]?.classList?.remove('visible');
                anchorBoxes[0]?.classList?.add('hidden');
                return;
            }

        }

        function cleanup() {
            anchorBoxes[0]?.classList?.remove('hidden', 'visible');
            anchorBoxes[1]?.classList?.remove('hidden', 'visible');
            window.removeEventListener('scroll', handleScroll);
            prepped = false;
        }

        // register a resize listener to add and cleanup the scroll event listener
        window.addEventListener('resize', () => {
            if (window.innerWidth < 782) {
                if (prepped === true) {
                    return cleanup();
                } else {
                    return;
                }
            } else if (prepped === false) {
                prepScroll();
            }
        });

        if ( window.innerWidth > 782 ) {
            prepScroll();
        } else {
            cleanup();
        }
    }

    /**
     * Updates the active tag based on the event detail.
     *
     * This method is responsible for updating the display and interaction properties
     * of the anchor boxes based on the active state of a tag. It modifies the styles
     * of the primary and secondary anchor boxes to either show or hide them depending
     * on the active state.
     *
     * @param {Event} e - The event object containing the detail with the active tag.
     * @property {Object} e.detail - The detail object of the event.
     * @property {boolean|null} e.detail.active - The active state of the tag.
     */
    #updateActiveTag(e) {
        consoleLogData('anchor-box.js #updateActiveTag firing...');

        // Extract the active state from the event detail, defaulting to null if not provided
        const active = e.detail.active ?? null;

        // If the active state is null, reset the styles of the primary and secondary anchor boxes
        if (active === null) {
            // Reset the display style of the primary anchor box to its default
            this.elemsArr[0].style.display = null;
            // Reset the opacity of the secondary anchor box to its default
            this.elemsArr[1].style.opacity = null;
            // Reset the pointer events of the secondary anchor box to its default
            this.elemsArr[1].style.pointerEvents = null;
        } else {
            // If the active state is not null, modify the styles to show the secondary anchor box

            // Hide the primary anchor box by setting its display style to 'none'
            this.elemsArr[0].style.display = 'none';
            // Make the secondary anchor box fully opaque
            this.elemsArr[1].style.opacity = 1;
            // Enable pointer events for the secondary anchor box
            this.elemsArr[1].style.pointerEvents = 'auto';
        }
    }
}
